import { useState, useEffect, useCallback } from 'react';
import { MCPServer } from '@/shared/types';
import { mcpApi } from '../../../services/mcpApi';

export const useMCPServers = () => {
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [selectedServer, setSelectedServer] = useState<MCPServer | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load servers and tools on mount
  useEffect(() => {
    loadMCPServers();
  }, []);

  const loadMCPServers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await mcpApi.getServers();
      if (response.success && response.data) {
        setServers(response.data);
      } else {
        // Fallback to empty array if no servers found
        setServers([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load MCP servers');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAddServer = useCallback(() => {
    setSelectedServer(null);
    setError(null);
  }, []);

  const handleEditServer = useCallback((server: MCPServer) => {
    setSelectedServer(server);
    setError(null);
  }, []);

  const handleSaveServer = useCallback(async (formData: Partial<MCPServer>, isEditing: boolean) => {
    try {
      setLoading(true);
      
      if (isEditing && selectedServer) {
        // Update existing server
        const response = await mcpApi.updateServer(selectedServer.id, formData);
        if (response.success && response.data) {
          setServers(prev => prev.map(s => s.id === selectedServer.id ? response.data : s));
          setSelectedServer(response.data); // Keep the updated server selected
          return { success: true };
        } else {
          setError(response.error || 'Failed to update server');
          return { success: false, error: response.error || 'Failed to update server' };
        }
      } else {
        // Add new server
        const response = await mcpApi.createServer(formData as Omit<MCPServer, 'id' | 'createdAt' | 'updatedAt'>);
        if (response.success && response.data) {
          setServers(prev => [...prev, response.data]);
          setSelectedServer(response.data); // Select the newly created server
          return { success: true };
        } else {
          setError(response.error || 'Failed to create server');
          return { success: false, error: response.error || 'Failed to create server' };
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save server';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [selectedServer]);

  const handleDeleteServer = useCallback(async (serverId: string) => {
    if (!confirm('Are you sure you want to delete this MCP server?')) return { success: false };
    
    try {
      setLoading(true);
      const response = await mcpApi.deleteServer(serverId);
      if (response.success) {
        setServers(prev => prev.filter(s => s.id !== serverId));
        if (selectedServer?.id === serverId) {
          setSelectedServer(null);
        }
        return { success: true };
      } else {
        setError(response.error || 'Failed to delete server');
        return { success: false, error: response.error || 'Failed to delete server' };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete server';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [selectedServer]);

  const handleToggleServer = useCallback(async (serverId: string) => {
    try {
      setLoading(true);
      const response = await mcpApi.toggleServer(serverId);
      if (response.success) {
        setServers(prev => prev.map(s => 
          s.id === serverId 
            ? { ...s, enabled: !s.enabled, status: !s.enabled ? 'connected' : 'disconnected' }
            : s
        ));
        return { success: true };
      } else {
        setError(response.error || 'Failed to toggle server');
        return { success: false, error: response.error || 'Failed to toggle server' };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to toggle server';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const handleTestConnection = useCallback(async (server: MCPServer) => {
    try {
      setLoading(true);
      const response = await mcpApi.testConnection(server.id);
      if (response.success) {
        setServers(prev => prev.map(s => 
          s.id === server.id 
            ? { ...s, status: 'connected', lastConnected: new Date().toISOString() }
            : s
        ));
        return { success: true };
      } else {
        setServers(prev => prev.map(s => 
          s.id === server.id 
            ? { ...s, status: 'error', errorMessage: response.error || 'Connection failed' }
            : s
        ));
        return { success: false, error: response.error || 'Connection failed' };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Connection failed';
      setServers(prev => prev.map(s => 
        s.id === server.id 
          ? { ...s, status: 'error', errorMessage }
          : s
      ));
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelectServer = useCallback((server: MCPServer) => {
    setSelectedServer(server);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    servers,
    selectedServer,
    loading,
    error,
    loadMCPServers,
    handleAddServer,
    handleEditServer,
    handleSaveServer,
    handleDeleteServer,
    handleToggleServer,
    handleTestConnection,
    handleSelectServer,
    clearError,
  };
};
